import React, { useEffect } from 'react';
import MainLayout from '../utilities/layouts/MainLayout';
import { Typography, useTheme, useMediaQuery } from '@material-ui/core';
import { PageTitleBlock } from '../utilities/commonUtilities/GenericDivElements';
import CalendarLayout from '../utilities/layouts/CalendarLayout';
import { styleBreakMobile, styleBreakSmallMobile } from '../utilities/layouts/Globals';
import { connect } from 'react-redux';
import { LoadingStatus, LoadingResult } from '../../utils/LoadingUtils';
import { retrieveAllVisits, setAcknowledgeFirstTimeCalendarAccess } from '../../actions';
import { RequiresLogin } from '../utilities/commonUtilities/RequiresLogin';
import IsLoadingPage from '../IsLoadingPage';
import LoadingErrorPage from '../LoadingErrorPage';
import { isoMoment } from '../utilities/commonUtilities/dateTools';
import TutorialWelcomePage from '../tutorials/TutorialWelcomePage';
import { call, select, takeLatest } from 'redux-saga/effects';
import { getUserId } from '../../selectors';
import * as ProfileAPI from '../../apis/profile';
import { loginRetrieveSessionSaga } from '../../sagas/authenticationSaga';
import * as actions from '../../actions/actionTypes';

export const SEMINOMA_YEARS = 9;
export const NON_SEMINOMA_YEARS = 5;

function CalendarPageContent({ visits, isLoadingAllVisits, allVisitsLoadingResult, fetchAllVisits, patientDiagnosisDate }) {
    const theme = useTheme();
    const padding = `${theme.spacing(3.5)}px`;
    const stylesoverride = {
        container: {
            maxWidth: '100%',
            padding: `${padding} 53px`,
            overflowX: 'auto',
            [styleBreakMobile(theme)]: {
                padding: `${padding} 0 0 0`,
            },
            [styleBreakSmallMobile(theme)]: {
                marginLeft: `${padding}`,
                marginRight: `${padding}`,
                minWidth: '265px',
            },
        },
    };

    useEffect(() => {
        if (!visits) {
            fetchAllVisits();
        }
    }, [fetchAllVisits, visits]);

    if (isLoadingAllVisits) {
        return <IsLoadingPage />;
    } else {
        if (allVisitsLoadingResult === LoadingResult.FAILED) {
            return <LoadingErrorPage />;
        } else {
            const { seminomaLabel, dateOfDiagnosis, endOfTreatementDate } = getJourneyProfileInfo(visits, patientDiagnosisDate);
            return (
                <>
                    <MainLayout id="calendar-main-layout" stylesOverride={stylesoverride} noHiddenOverflowX={true}>
                        <PageTitleBlock>
                            <Typography variant="h1">Calendar</Typography>
                        </PageTitleBlock>

                        <CalendarLayout visits={visits} dateOfDiagnosis={dateOfDiagnosis} endOfTreatementDate={endOfTreatementDate} stage={seminomaLabel} />
                    </MainLayout>
                </>
            );
        }
    }
}

const mapStateToProps = state => {
    return {
        visits: state?.allVisits?.visits,
        isLoadingAllVisits: state?.dataLoading?.allVisits?.loading !== LoadingStatus.ENDED,
        allVisitsLoadingResult: state?.dataLoading?.allVisits?.loadingResult,
        patientDiagnosisDate: state?.login?.session?.extraFields?.diagnosisDate ?? '',
        hasCalendarTutorialBeenShown: state?.login?.session?.extraFields?.hasAccessedCalendarOnce === 'true',
    };
};

const mapDispatchToProps = dispatch => {
    const fetchAllVisits = () => {
        dispatch(retrieveAllVisits());
    };
    return {
        fetchAllVisits,
    };
};

const CalendarPage = connect(mapStateToProps, mapDispatchToProps)(CalendarPageContent);

// This is the "Guarded" calendar page.  I believe "Guarded" just means that it checks to see if a tutorial needs to be shown.
const mapStateToPropsForGuardedPage = state => {
    return {
        hasCalendarTutorialBeenShown: state?.login?.session?.extraFields?.hasAccessedCalendarOnce === 'true',
    };
};
const mapDispatchToPropsForGuardedPage = dispatch => {
    const acknowledgeCalendarTutorial = () => {
        dispatch(setAcknowledgeFirstTimeCalendarAccess());
    };
    return {
        acknowledgeCalendarTutorial,
    };
};

export default connect(mapStateToPropsForGuardedPage, mapDispatchToPropsForGuardedPage)(GuardedCalendarPage);

function GuardedCalendarPage({ isShowTutorial, hasCalendarTutorialBeenShown, acknowledgeCalendarTutorial }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(styleBreakMobile(theme));
    const NUMBER_CALENDAR_TUTORIAL_SEGMENTS = isMobile ? 3 : 12;
    const tutorialPageSubtitleText = 'Welcome to your Watchman Calendar';
    const tutorialPageBodyText = "To help you prepare, this will show you where you are in your journey, and what's coming up next.";

    const tutorialCompletedCallback = function() {
        if (!hasCalendarTutorialBeenShown) {
            acknowledgeCalendarTutorial();
        }
    };

    if (isShowTutorial || !hasCalendarTutorialBeenShown) {
        return (
            <TutorialWelcomePage
                subtitleText={tutorialPageSubtitleText}
                bodyText={tutorialPageBodyText}
                setLaunchTutorial={tutorialCompletedCallback}
                noOfPages={NUMBER_CALENDAR_TUTORIAL_SEGMENTS}
                isMobile={isMobile}
                type={'Calendar'}
            />
        );
    } else
        return (
            <RequiresLogin>
                <CalendarPage />
            </RequiresLogin>
        );
}

export function getJourneyProfileInfo(visits, patientDiagnosisDate) {
    let seminomaLabel = '';
    // The seminoma label comes from the treatment group of the ONBOARDING visit.
    const onboardingVisits = visits.filter(visit => visit.visitType === 'ONBOARDING');
    if (onboardingVisits?.length > 0) {
        // Get the first one if there are multiple.
        seminomaLabel = onboardingVisits[0].treatmentGroup;
    }

    const patientDiagnosisDateIsoMoment = isoMoment(patientDiagnosisDate);
    // If there is a valid diagnosis date for the patient, use it.  If not, use the first visit as the date of diagnosis.
    const dateOfDiagnosis = patientDiagnosisDateIsoMoment.isValid() ? patientDiagnosisDateIsoMoment : isoMoment(visits?.[0]?.taskOpenDate ?? undefined);
    const endOfTreatementDate = isoMoment(visits?.[visits.length - 1]?.taskOpenDate ?? undefined);

    return { seminomaLabel, dateOfDiagnosis, endOfTreatementDate };
}

// These are the sagas used with marking that the tutorial has been viewed and reloading the state.
export function* acknowledgeFirstTimeCalendarAccessSaga() {
    try {
        const userId = yield select(getUserId);
        yield call(ProfileAPI.setAcknowledgeFirstTimeCalendarAccess, userId);
        // Refresh session.
        yield call(loginRetrieveSessionSaga);
    } catch (error) {
        console.dir(error);
    }
}

export function* watchAcknowledgeFirstTimeCalendarAccessSaga() {
    yield takeLatest(actions.CALENDAR_ACKNOWLEDGE_FIRST_TIME_ACCESS, acknowledgeFirstTimeCalendarAccessSaga);
}
